import {
	CONTRACT_EXTRACTABLE_EXTENSIONS,
	ContractSuretyType,
	FileType,
	fileId,
	type SuretyAccountId,
	type SuretyQuoteId,
} from "@inrev/common";
import { bondRequestAttachmentsQueryKey, bondRequestsQueryKey } from "@portal/api/bond-request";
import { useAttachmentUpload } from "@portal/api/bond-request/agent";
import { FileUploadStatus } from "@portal/api/files";
import { FileUploadStatusIndicator } from "@portal/components/features/file-upload";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Dropzone } from "@portal/components/patterns/dropzone";
import { Text } from "@portal/components/primitives";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { v4 } from "uuid";
import { getNameAndExtensionFromFileName } from "../../../../../utils";
import { getMimeTypes } from "../../../../../utils/mime";

export type ContractRfpUploadModalProps = {
	accountId: SuretyAccountId;
	quoteId: SuretyQuoteId;
	contractSuretyType: ContractSuretyType;
	onClose: () => void;
};

type ContractRfpFileType = typeof FileType.bid_package | typeof FileType.contract;

export const ContractRfpUploadModal: FC<ContractRfpUploadModalProps> = ({
	accountId,
	quoteId,
	contractSuretyType,
	onClose,
}) => {
	const [isUploading, setIsUploading] = useState(false);
	const isBid = contractSuretyType === ContractSuretyType.bid;
	const fileType: ContractRfpFileType = isBid ? FileType.bid_package : FileType.contract;
	const label = isBid ? "RFP / Bid Package" : "Contract";
	// Built at render, not module load: `docx` resolves only after `initializeMimeTypes()` runs at bootstrap.
	const accept = useMemo(() => getMimeTypes([...CONTRACT_EXTRACTABLE_EXTENSIONS]), []);

	const invalidateQueryKeys = useMemo(
		() => [bondRequestAttachmentsQueryKey(quoteId), bondRequestsQueryKey(quoteId)],
		[quoteId],
	);

	const { uploadFn, fileLoading } = useAttachmentUpload<ContractRfpFileType>(
		`/v2/surety/accounts/${accountId}/quotes/${quoteId}/attachments`,
		{
			onUploadSuccess: useCallback(() => {
				setIsUploading(false);
				onClose();
			}, [onClose]),
			onUploadError: useCallback(() => {
				setIsUploading(false);
			}, []),
		},
		invalidateQueryKeys,
	);

	const handleFiles = useCallback(
		(files: File[]) => {
			const file = files[0];
			if (!file) return;

			const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
			setIsUploading(true);
			uploadFn({
				fileUploads: [
					{
						rawFile: file,
						pendingFile: {
							id: fileId.parse(v4()),
							name: fileName,
							extension,
							types: [fileType],
						},
					},
				],
			});
		},
		[fileType, uploadFn],
	);

	const isBusy = isUploading || fileLoading;

	return (
		<Modal onClickOutside={isBusy ? undefined : onClose}>
			<ModalItemWithHeader header={`Upload ${label}`} onClose={onClose} closeDisabled={isBusy}>
				<div
					data-testid="contract-rfp-upload-modal"
					className="flex min-w-[480px] flex-col gap-4 px-5 py-4"
				>
					<Text type="body" color="primary">
						Upload a copy of the {isBid ? "RFP / Bid Package" : "contract"}.
					</Text>
					<Dropzone
						testId="contract-rfp-dropzone"
						accept={accept}
						disabled={isBusy}
						onDrop={handleFiles}
						placeholder={`Drag and drop or click to select your ${label}`}
					/>
					{isBusy && (
						<div
							className="flex items-center gap-2"
							aria-live="polite"
							data-testid="contract-rfp-uploading"
						>
							<FileUploadStatusIndicator
								status={FileUploadStatus.uploading}
								id="contract-rfp-upload"
							/>
							<Text type="body" color="secondary">
								Uploading {label}…
							</Text>
						</div>
					)}
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
